import React from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  SUBSCRIPTION_ADD,
  SUBSCRIPTION_EDIT,
} from "../../../../services/ApiCalls";
import { useNavigate } from "react-router-dom";

function SubscriptionForm({ data }) {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    getValues,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: data?.name || "",
      price: data?.price || "",
      commission: data?.commission || "",
      description: data?.description || "",
      features: data?.features || [""],
      interval: data?.interval || "",
      status: data?.status || "inactive",
      _id: data?._id || "",
      noOfSessions: data?.noOfSessions || "",
    },
  });

  const onSubmit = async (formData) => {
    try {
      let response;
      if (data?._id) {
        response = await SUBSCRIPTION_EDIT(formData);
      } else {
        delete formData._id;
        response = await SUBSCRIPTION_ADD(formData);
      }

      const { status, message } = response?.data || {};
      if (status === "failure")
        throw new Error(message || "Something went wrong!");

      toast.success(message || "Submitted successfully!");
      navigate("/subscription");
    } catch (error) {
      toast.error(error?.message || "Failed to submit");
    }
  };

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      className="mx-auto"
      style={{ maxWidth: 777 }}
    >
      <Row>
        <Col lg="6" className="my-2">
          <Form.Group controlId="name">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              {...register("name", { required: "Name is required" })}
              isInvalid={!!errors.name}
            />
            {errors.name && (
              <div className="text-danger">{errors.name.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col lg="6" className="my-2">
          <Form.Group controlId="description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              {...register("description", {
                required: "Description is required",
              })}
              isInvalid={!!errors.description}
            />
            {errors.description && (
              <div className="text-danger">{errors.description.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col lg="6" className="my-2">
          <Form.Group controlId="price">
            <Form.Label>Price</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="1"
              {...register("price", {
                required: "Price is required",
                valueAsNumber: true,
                min: { value: 0, message: "Price cannot be negative" },
              })}
              isInvalid={!!errors.price}
              onKeyDown={(e) =>
                ["e", "-"].includes(e.key) && e.preventDefault()
              }
            />
            {errors.price && (
              <div className="text-danger">{errors.price.message}</div>
            )}
          </Form.Group>
        </Col>
        <Col lg="6" className="my-2">
          <Form.Group controlId="noOfSessions">
            <Form.Label>No of Sessions</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="1"
              {...register("noOfSessions", {
                required: "No of Sessions is required",
                valueAsNumber: true,
                min: { value: 1, message: "No of Sessions cannot be less than 1" },
              })}
              isInvalid={!!errors.noOfSessions}
              onKeyDown={(e) =>
                ["e", "-"].includes(e.key) && e.preventDefault()
              }
            />
            {errors.noOfSessions && (
              <div className="text-danger">{errors.noOfSessions.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col lg="6" className="my-2">
          <Form.Group controlId="commission">
            <Form.Label>Commission (%)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              max="100"
              step="0.01"
              {...register("commission", {
                required: "Commission percentage is required",
                valueAsNumber: true,
                min: { value: 0, message: "Commission cannot be negative" },
                max: { value: 100, message: "Max is 100%" },
              })}
              isInvalid={!!errors.commission}
              onKeyDown={(e) =>
                ["e", "-"].includes(e.key) && e.preventDefault()
              }
            />
            {errors.commission && (
              <div className="text-danger">{errors.commission.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col lg="6" className="my-2">
          <Form.Group controlId="interval">
            <Form.Label>Interval</Form.Label>
            <Form.Select
              {...register("interval", { required: "Interval is required" })}
              isInvalid={!!errors.interval}
            >
              <option value="">-- Select Interval --</option>
              {/* <option value="daily">Daily</option> */}
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </Form.Select>
            {errors.interval && (
              <div className="text-danger">{errors.interval.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col lg="12" className="my-2">
          <Form.Group controlId="features">
            <Form.Label>Features</Form.Label>
            {watch("features").map((_, index) => (
              <Row key={index} className="mb-2">
                <Col lg={10}>
                  <Form.Control
                    type="text"
                    placeholder={`Feature ${index + 1}`}
                    {...register(`features.${index}`, {
                      required: "Feature is required",
                    })}
                    isInvalid={!!errors.features?.[index]}
                  />
                  {errors.features?.[index] && (
                    <div className="text-danger">
                      {errors.features[index]?.message}
                    </div>
                  )}
                </Col>
                <Col lg={2}>
                  <Button
                    variant="danger"
                    onClick={() => {
                      const current = getValues("features");
                      setValue(
                        "features",
                        current.filter((_, i) => i !== index)
                      );
                    }}
                  >
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}
            <Button
              variant="secondary"
              onClick={() =>
                setValue("features", [...getValues("features"), ""])
              }
            >
              + Add Feature
            </Button>
          </Form.Group>
        </Col>

        <Col lg="6" className="my-2">
          <Form.Group controlId="status">
            <Form.Label>Is Active</Form.Label>
            <Controller
              name="status"
              control={control}
              rules={{ required: "Active/Inactive is required" }}
              render={({ field }) => (
                <Form.Check
                  type="switch"
                  id="active-toggle"
                  checked={field.value === "active"}
                  onChange={(e) =>
                    field.onChange(e.target.checked ? "active" : "inactive")
                  }
                />
              )}
            />
            {errors.status && (
              <div className="text-danger">{errors.status.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col lg="12" className="my-2">
          <Button variant="primary" type="submit">
            Submit
          </Button>
        </Col>
      </Row>
    </Form>
  );
}

export default SubscriptionForm;
